import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  BookOpen,
  DollarSign,
  Activity,
  Target,
  RefreshCw
} from 'lucide-react';
import VideoAnalyticsDashboard from './VideoAnalyticsDashboard';

interface DashboardData {
  metrics: {
    totalStudents: number;
    totalCourses: number;
    totalEnrollments: number;
    totalRevenue: number;
    activeUsers: number;
    studentsGrowth: number;
    enrollmentsGrowth: number;
    revenueGrowth: number;
  };
  charts: Array<{
    period: string;
    students: number;
    enrollments: number;
    revenue: number;
  }>;
  topCourses: Array<{
    id: string;
    title: string;
    students: number;
    revenue: number;
    completion: number;
  }>;
  userActivity: {
    activeToday: number;
    avgSessions: number;
    avgTime: number;
    completionRate: number;
  };
  revenue: {
    courses: number;
    subscriptions: number;
    certifications: number;
  };
  engagement: {
    averageEngagement: number;
    deviceDistribution: Array<{
      device: string;
      count: number;
    }>;
    speedDistribution: Array<{
      speed: number;
      count: number;
    }>;
  };
}


interface RevenueAnalytics {
  totalRevenue: number;
  monthlyGrowth: number;
  averageOrderValue: number;
  monthlyRevenue: Array<{
    month: string;
    revenue: number;
    enrollments: number;
    growth: number;
  }>;
  courseRevenue: Array<{
    courseId: string;
    title: string;
    revenue: number;
    enrollments: number;
    averagePrice: number;
  }>;
}

const ComprehensiveAnalyticsDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState<RevenueAnalytics | null>(null);
  const [selectedView, setSelectedView] = useState<'overview' | 'students' | 'courses' | 'revenue' | 'engagement'>('overview');
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

  useEffect(() => {
    loadDashboardData();
  }, [selectedPeriod]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardResponse, revenueResponse] = await Promise.all([
        fetch('https://api.escuela.agentesia.cloud/api/v1/analytics/dashboard-summary', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`https://api.escuela.agentesia.cloud/api/v1/analytics/revenue-analytics?period=${selectedPeriod}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (dashboardResponse.ok && revenueResponse.ok) {
        const dashboard = await dashboardResponse.json();
        const revenue = await revenueResponse.json();
        
        setDashboardData(dashboard.data);
        setRevenueAnalytics(revenue.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatGrowth = (growth: number) => {
    const isPositive = growth >= 0;
    return (
      <span className={`flex items-center ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
        <span className="ml-1">{Math.abs(growth).toFixed(1)}%</span>
      </span>
    );
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    growth?: number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, growth, icon, color }) => (
    <div className="bg-card text-card-foreground rounded-lg p-6 shadow-sm border border-border">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
          {growth !== undefined && (
            <div className="mt-2">
              {formatGrowth(growth)}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 bg-background min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-background min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Última actualización: {lastUpdated.toLocaleString('es-ES')}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className="bg-card border border-border rounded-md px-4 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
          </select>
          <button
            onClick={loadDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
          >
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </button>
        </div>
      </div>

      {/* View Selector */}
      <div className="flex space-x-2 mb-8">
        {[
          { key: 'overview', label: 'Resumen', icon: Activity },
          { key: 'students', label: 'Estudiantes', icon: Users },
          { key: 'courses', label: 'Cursos', icon: BookOpen },
          { key: 'revenue', label: 'Ingresos', icon: DollarSign },
          { key: 'engagement', label: 'Engagement', icon: Target }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSelectedView(key as any)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              selectedView === key
                ? 'bg-primary text-primary-foreground'
                : 'bg-card hover:bg-muted border border-border text-foreground'
            }`}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {dashboardData && (
        <>
          {/* Overview Section */}
          {selectedView === 'overview' && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <MetricCard
                  title="Total Estudiantes"
                  value={dashboardData.metrics.totalStudents.toLocaleString()}
                  growth={dashboardData.metrics.studentsGrowth}
                  icon={<Users className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <MetricCard
                  title="Cursos Publicados"
                  value={dashboardData.metrics.totalCourses}
                  icon={<BookOpen className="w-6 h-6 text-white" />}
                  color="bg-green-500"
                />
                <MetricCard
                  title="Ingresos Totales"
                  value={formatCurrency(dashboardData.metrics.totalRevenue)}
                  growth={dashboardData.metrics.revenueGrowth}
                  icon={<DollarSign className="w-6 h-6 text-white" />}
                  color="bg-yellow-500"
                />
                <MetricCard
                  title="Usuarios Activos"
                  value={dashboardData.metrics.activeUsers}
                  icon={<Activity className="w-6 h-6 text-white" />}
                  color="bg-purple-500"
                />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Crecimiento Mensual</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dashboardData.charts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="students" stroke="#06b6d4" strokeWidth={2} />
                      <Line type="monotone" dataKey="enrollments" stroke="#8b5cf6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Distribución de Dispositivos</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={dashboardData.engagement.deviceDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        label={({ device, count }) => `${device}: ${count}`}
                      >
                        {dashboardData.engagement.deviceDistribution.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Courses */}
              <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Cursos Más Populares</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-foreground">Curso</th>
                        <th className="text-center py-3 px-4 text-foreground">Estudiantes</th>
                        <th className="text-center py-3 px-4 text-foreground">Ingresos</th>
                        <th className="text-center py-3 px-4 text-foreground">Finalización</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.topCourses.map((course, index) => (
                        <tr key={course.id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-primary mr-2">#{index + 1}</span>
                              <span className="text-foreground">{course.title}</span>
                            </div>
                          </td>
                          <td className="text-center py-3 px-4 text-foreground">{course.students}</td>
                          <td className="text-center py-3 px-4 text-foreground">{formatCurrency(course.revenue)}</td>
                          <td className="text-center py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              course.completion >= 70 ? 'bg-green-100 text-green-800' :
                              course.completion >= 40 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {course.completion}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Students Section */}
          {selectedView === 'students' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Estudiantes"
                  value={dashboardData.metrics.totalStudents.toLocaleString()}
                  growth={dashboardData.metrics.studentsGrowth}
                  icon={<Users className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <MetricCard
                  title="Estudiantes Activos"
                  value={dashboardData.metrics.activeUsers.toLocaleString()}
                  icon={<Activity className="w-6 h-6 text-white" />}
                  color="bg-green-500"
                />
                <MetricCard
                  title="Inscripciones Totales"
                  value={dashboardData.metrics.totalEnrollments.toLocaleString()}
                  growth={dashboardData.metrics.enrollmentsGrowth}
                  icon={<BookOpen className="w-6 h-6 text-white" />}
                  color="bg-purple-500"
                />
                <MetricCard
                  title="Tasa de Finalización"
                  value="78%"
                  icon={<Target className="w-6 h-6 text-white" />}
                  color="bg-orange-500"
                />
              </div>

              <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Crecimiento de Estudiantes</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dashboardData.charts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="students" stroke="#06b6d4" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Courses Section */}
          {selectedView === 'courses' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Cursos"
                  value={dashboardData.metrics.totalCourses.toString()}
                  icon={<BookOpen className="w-6 h-6 text-white" />}
                  color="bg-purple-500"
                />
                <MetricCard
                  title="Cursos Populares"
                  value={dashboardData.topCourses.filter(c => c.students > 100).length.toString()}
                  icon={<TrendingUp className="w-6 h-6 text-white" />}
                  color="bg-green-500"
                />
                <MetricCard
                  title="Promedio Estudiantes/Curso"
                  value={Math.round(dashboardData.metrics.totalEnrollments / dashboardData.metrics.totalCourses).toString()}
                  icon={<Users className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <MetricCard
                  title="Tasa de Inscripción"
                  value="85%"
                  icon={<Target className="w-6 h-6 text-white" />}
                  color="bg-orange-500"
                />
              </div>

              <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Top Cursos por Estudiantes</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-foreground">Curso</th>
                        <th className="text-left py-3 px-4 font-medium text-foreground">Estudiantes</th>
                        <th className="text-left py-3 px-4 font-medium text-foreground">Inscripciones</th>
                        <th className="text-left py-3 px-4 font-medium text-foreground">Finalización</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.topCourses.map((course) => (
                        <tr key={course.id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4 text-foreground">{course.title}</td>
                          <td className="py-3 px-4 text-foreground">{course.students.toLocaleString()}</td>
                          <td className="py-3 px-4 text-foreground">{course.students.toLocaleString()}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              course.completion >= 70 ? 'bg-green-100 text-green-800' :
                              course.completion >= 40 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {course.completion}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Revenue Section */}
          {selectedView === 'revenue' && revenueAnalytics && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                  title="Ingresos Totales"
                  value={formatCurrency(revenueAnalytics.totalRevenue)}
                  growth={revenueAnalytics.monthlyGrowth}
                  icon={<DollarSign className="w-6 h-6 text-white" />}
                  color="bg-green-500"
                />
                <MetricCard
                  title="Valor Promedio Pedido"
                  value={formatCurrency(revenueAnalytics.averageOrderValue)}
                  icon={<Target className="w-6 h-6 text-white" />}
                  color="bg-blue-500"
                />
                <MetricCard
                  title="Cursos con Ingresos"
                  value={revenueAnalytics.courseRevenue.length}
                  icon={<BookOpen className="w-6 h-6 text-white" />}
                  color="bg-purple-500"
                />
              </div>

              <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Ingresos por Mes</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={revenueAnalytics.monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => [formatCurrency(value), 'Ingresos']} />
                    <Area type="monotone" dataKey="revenue" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Ingresos por Curso</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={revenueAnalytics.courseRevenue.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="title" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip formatter={(value: number) => [formatCurrency(value), 'Ingresos']} />
                    <Bar dataKey="revenue" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Engagement Section */}
          {selectedView === 'engagement' && (
            <VideoAnalyticsDashboard />
          )}
        </>
      )}
    </div>
  );
};

export default ComprehensiveAnalyticsDashboard;